"use client"

import Coupon from '@/src/interfaces/benefits/coupon'
import ShoppingCart from '@/src/interfaces/system/shopping-cart'
import React, { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react'
import { MERCHANT_STORE_NAME } from '@/src/constants/defaults'
import { usePathname } from 'next/navigation'
import MerchantDb from '@/src/core/merchant-db'

const KEY_PATH = 'cart'

interface ShoppingCartContextType {
    shoppingCart: ShoppingCart.Cart | undefined
    totalItemsPrice: number
    totalPriceWithDiscount: number
    totalDiscountPrice: number
    cashbackDiscountPrice: number
    updateProduct: (item: ShoppingCart.CartItem, quantity: number, index: number) => void
    deleteProduct: (itemIndex: number) => void
    addProduct: (item: ShoppingCart.CartItem) => void
    addFee(fee: number): void
    addCashback(cashback: ShoppingCart.Cashback): void
    removeCashback(): void
    removeRedeemedItemsAndCashback(): void
    addCoupon: (coupon: Coupon.Response) => void
    removeCoupon: () => void
    clearShoppingCartDb(): void
}

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(undefined)

const saveCartToDb = async (cart: ShoppingCart.Cart) => {
    const db = await MerchantDb.getDB()

    if (db)
        await db.put(MERCHANT_STORE_NAME, { id: KEY_PATH, ...cart })
}

const loadCartFromDb = async (): Promise<ShoppingCart.Cart | undefined> => {
    const db = await MerchantDb.getDB()

    if (db)
        return await db.get(MERCHANT_STORE_NAME, KEY_PATH)
}

const clearCartInDb = async () => {
    const db = await MerchantDb.getDB()

    if (db)
        await db.delete(MERCHANT_STORE_NAME, KEY_PATH)
}

export const ShoppingCartProvider: React.FC<{ children: ReactNode, initialShoppingCart?: ShoppingCart.Cart }> = ({ children, initialShoppingCart }) => {
    const [shoppingCart, setShoppingCart] = useState<ShoppingCart.Cart | undefined>(initialShoppingCart)
    const [totalItemsPrice, setTotalItemsPrice] = useState(0)
    const [totalPriceWithDiscount, setTotalPriceWithDiscount] = useState(0)
    const [totalDiscountPrice, setTotalDiscountPrice] = useState(0)
    const [cashbackDiscountPrice, setCashbackDiscountPrice] = useState(0)
    const guid = crypto.randomUUID()
    const pathname = usePathname()

    const clearShoppingCartDb = useCallback(async () => {
        await clearCartInDb()
        setShoppingCart(undefined)
    }, [])

    const addCoupon = useCallback((coupon: Coupon.Response) => {
        if (!shoppingCart) return
        setShoppingCart({ ...shoppingCart, coupon: coupon })
    }, [shoppingCart])

    const removeCoupon = useCallback(() => {
        if (!shoppingCart) return
        setShoppingCart({ ...shoppingCart, coupon: undefined })
    }, [shoppingCart])

    const addFee = useCallback((fee: number) => {
        if (!shoppingCart) return
        setShoppingCart({ ...shoppingCart, fee: fee })
    }, [shoppingCart])

    const addCashback = (cashback: ShoppingCart.Cashback) => {
        if (!shoppingCart) return

        setShoppingCart({
            ...shoppingCart,
            cashback: {
                loyaltyCampaignId: cashback.loyaltyCampaignId,
                cashbackValue: cashback.cashbackValue,
            }
        })
    }

    const removeCashback = useCallback(() => {
        if (!shoppingCart) return

        setShoppingCart({
            ...shoppingCart,
            cashback: undefined
        })

        setCashbackDiscountPrice(0)
    }, [shoppingCart])

    const removeRedeemedItemsAndCashback = useCallback(() => {
        if (!shoppingCart?.items) return

        setShoppingCart({
            ...shoppingCart,
            items: shoppingCart.items.filter(i => !i.loyaltyCampaignId),
            cashback: undefined
        })
    }, [shoppingCart])

    const addProduct = useCallback((item: ShoppingCart.CartItem) => {
        setShoppingCart((prevCart) => {
            const items = prevCart?.items ?? []
            return {
                ...prevCart,
                guid: prevCart?.guid ?? guid,
                items: [...items, item]
            }
        })
    }, [guid])

    const updateProduct = (item: ShoppingCart.CartItem, quantity: number, index: number) => {
        setShoppingCart(prevCart => {
            if (!prevCart) return prevCart

            const oldQty = item.quantity

            if (oldQty !== quantity) {
                item.quantity = quantity
                const totalPricebByUnit = (item.totalPrice ?? 0) / oldQty
                item.totalPrice = totalPricebByUnit * quantity
            }

            return {
                ...prevCart,
                items: prevCart.items.map((p, i) => p.id === item.id && i === index ? item : p)
            }
        })
    }

    const deleteProduct = useCallback((itemIndex: number) => {
        setShoppingCart(prevCart => {
            const currentItem = prevCart?.items.at(itemIndex)

            if (!currentItem) return

            if (!prevCart) return prevCart

            const updatedProducts = [...prevCart.items]
            updatedProducts.splice(itemIndex, 1)

            return {
                ...shoppingCart,
                guid: guid,
                items: updatedProducts,
            }
        })
    }, [shoppingCart])

    const getCashbackDiscount = useCallback((): number => {
        return shoppingCart?.cashback?.cashbackValue ?? 0
    }, [shoppingCart])

    const getTotalPriceWithDiscount = (): number => {
        if (!shoppingCart) return 0

        let totalPrice = getTotalItemsPrice()

        if (shoppingCart.coupon)
            totalPrice = getTotalPriceWithCoupon('totalItemsPrice')
        else
            totalPrice += (shoppingCart.fee ?? 0)

        totalPrice -= cashbackDiscountPrice

        return totalPrice
    }

    const getTotalDiscountPrice = (): number => {
        if (!shoppingCart) return 0

        if (shoppingCart.coupon)
            return getTotalPriceWithCoupon('discountPrice')

        return cashbackDiscountPrice
    }

    const getTotalPriceWithCoupon = (calculate: 'discountPrice' | 'totalItemsPrice'): number => {
        if (!shoppingCart?.coupon) return 0

        const { applyType, discountType, value } = shoppingCart.coupon

        let deliveryFee = (shoppingCart.fee ?? 0)
        let itemsPrice = getTotalItemsPrice()
        let totalOrder = itemsPrice + deliveryFee

        const calculateDiscount = (base: number) => {
            return discountType === 'Percentual'
                ? (base * (value / 100)).discountRoundAwayFromZero()
                : value
        }

        if (calculate === 'discountPrice') {
            if (discountType === 'Percentual') {
                switch (applyType) {
                    case 'Valor dos Itens':
                        return calculateDiscount(itemsPrice)
                    case 'Valor Total':
                        return calculateDiscount(totalOrder)
                    case 'Valor da Entrega':
                        return calculateDiscount(deliveryFee)
                }
            }
            return value
        }
        else {
            switch (applyType) {
                case 'Valor dos Itens':
                    if (discountType === 'Percentual') {
                        return itemsPrice - calculateDiscount(itemsPrice) + deliveryFee
                    }
                    else {
                        if (value >= itemsPrice)
                            return deliveryFee

                        return totalOrder - value
                    }
                case 'Valor Total':
                    if (discountType === 'Percentual')
                        return totalOrder - calculateDiscount(totalOrder)
                    else
                        return totalOrder - value
                case 'Valor da Entrega':
                    if (discountType === 'Percentual') {
                        return totalOrder - calculateDiscount(deliveryFee)
                    }
                    else {
                        if (deliveryFee <= 0)
                            return totalOrder

                        const valueFee = deliveryFee < value ? deliveryFee : value

                        return totalOrder - valueFee
                    }
                default:
                    return totalOrder
            }
        }
    }

    const getTotalItemsPrice = useCallback((): number => {
        if (!shoppingCart?.items) return 0
        return shoppingCart.items.reduce((total, product) => total + (product.totalPrice ?? 0), 0)
    }, [shoppingCart])

    useEffect(() => {
        if (shoppingCart) {
            saveCartToDb(shoppingCart)
        }

        setCashbackDiscountPrice(getCashbackDiscount())
        setTotalItemsPrice(getTotalItemsPrice())
        setTotalPriceWithDiscount(getTotalPriceWithDiscount())
        setTotalDiscountPrice(getTotalDiscountPrice())
    }, [shoppingCart])

    useEffect(() => {
        if (shoppingCart) {
            const dayInMs = 86400000 // 24 horas
            setShoppingCart({ ...shoppingCart, expiresAt: Date.now() + dayInMs })
        }
    }, [shoppingCart?.items])

    const initShoppingCart = async () => {
        const cart = await loadCartFromDb()

        if (cart) {
            const currentDate = Date.now()
            const expiresAt = cart?.expiresAt ?? 0

            if (currentDate > expiresAt) {
                setShoppingCart(undefined)
                await clearShoppingCartDb()
            }
            else {
                setShoppingCart({ ...cart, fee: undefined })
            }
        }
        else {
            setShoppingCart({
                guid: guid,
                items: []
            })
        }
    }

    useEffect(() => {
        if (!shoppingCart)
            initShoppingCart()
    }, [pathname])

    useEffect(() => {
        initShoppingCart()
    }, [])

    return (
        <ShoppingCartContext.Provider value={{
            shoppingCart,
            addProduct,
            addCoupon,
            addFee,
            addCashback,
            removeCashback,
            removeCoupon,
            removeRedeemedItemsAndCashback,
            updateProduct,
            deleteProduct,
            clearShoppingCartDb,
            totalItemsPrice,
            totalPriceWithDiscount,
            totalDiscountPrice,
            cashbackDiscountPrice
        }}>
            {children}
        </ShoppingCartContext.Provider>
    )
}

export const useShoppingCart = (): ShoppingCartContextType => {
    const context = useContext(ShoppingCartContext)

    if (!context)
        throw new Error('O contexto do ShoppingCart deve estar dentro do ShoppingCartProvider.')

    return context
}
